import {
  AfterContentInit,
  Component,
  ContentChildren,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  forwardRef,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Marks a caller's own button as a pill tab. The button keeps its label, attributes and click handler; the strip
 * only owns the look and the sliding highlight.
 */
@Directive({
  selector: '[appPillTab]',
  host: {
    class: 'pill-tab',
    '[class.active]': 'active',
  },
})
export class PillTabDirective implements OnChanges {
  @Input() active = false;

  readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly strip = inject(forwardRef(() => PillTabsComponent), { optional: true });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active']) {
      this.strip?.scheduleMeasure();
    }
  }
}

@Component({
  selector: 'app-pill-tabs',
  template: `
    <div class="slider" [class.ready]="ready()" [style.left.px]="left()" [style.width.px]="width()"></div>
    <ng-content />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pill-tabs.component.scss'],
})
export class PillTabsComponent implements AfterContentInit {
  @ContentChildren(PillTabDirective, { descendants: true })
  private readonly tabs?: QueryList<PillTabDirective>;

  readonly left = signal(0);
  readonly width = signal(0);
  readonly ready = signal(false);

  private readonly destroyRef = inject(DestroyRef);

  // A label can change width without the tab set or the active tab changing (a count loading in), which neither
  // the QueryList nor the active input would report.
  private readonly resizeObserver =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.scheduleMeasure());

  ngAfterContentInit(): void {
    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
    this.observeTabs();

    // Tabs are often rendered from data that arrives later, so the highlight follows the set as it changes.
    this.tabs?.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.observeTabs();
      this.scheduleMeasure();
    });

    // Measured after layout, and the transition switched on only then, so the first paint does not slide in.
    queueMicrotask(() => {
      this.measure();
      this.ready.set(true);
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.measure();
  }

  private observeTabs(): void {
    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
    this.tabs?.forEach((t) => this.resizeObserver!.observe(t.element.nativeElement));
  }

  scheduleMeasure(): void {
    queueMicrotask(() => this.measure());
  }

  private measure(): void {
    const tab = this.tabs?.find((t) => t.active) ?? this.tabs?.first;
    const element = tab?.element.nativeElement;
    if (!element) {
      return;
    }

    this.left.set(element.offsetLeft);
    this.width.set(element.offsetWidth);
  }
}
